import { React, createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#e0e0e0",
          200: "#c2c2c2",
          300: "#a3a3a3",
          400: "#858585",
          500: "#666666",
          600: "#525252",
          700: "#3d3d3d",
          800: "#292929",
          900: "#141414",
        },
        primary: {
          100: "#d0d1d5",
          200: "#a1a4ab",
          300: "#727681",
          400: "#1F2A40",
          500: "#141b2d",
          600: "#101624",
          700: "#0c101b",
          800: "#080b12",
          900: "#000000",
        },
        greenAccent: {
          100: "#dbf5ee",
          200: "#b7ebde",
          300: "#94e2cd",
          400: "#f1f1f1",
          500: "#f1f1f1",
          600: "#3872FA",
          700: "#2e7c67",
          800: "#f1f1f1",
          900: "#696762",
        },
        redAccent: {
          100: "#f8dcdb",
          200: "#f1b9b7",
          300: "#e99592",
          400: "#e2726e",
          500: "#db4f4a",
          600: "#af3f3b",
          700: "#832f2c",
          800: "#58201e",
          900: "#2c100f",
        },
        blueAccent: {
          100: "#e1e2fe",
          200: "#f1f1f1",
          300: "#a4a9fc",
          400: "#ffffff",
          500: "#6870fa",
          600: "#535ac8",
          700: "#696762",
          800: "#2a2d64",
          900: "#151632",
        },
      }
    : {
        grey: {
          100: "#141414",
          200: "#292929",
          300: "#3d3d3d",
          400: "#525252",
          500: "#666666",
          600: "#858585",
          700: "#a3a3a3",
          800: "#c2c2c2",
          900: "#e0e0e0",
        },
        primary: {
          100: "#F1F1F1",
          200: "#080b12",
          300: "#0c101b",
          400: "#ffffff",
          500: "#141b2d",
          600: "#1F2A40",
          700: "#727681",
          800: "#a1a4ab",
          900: "#d0d1d5",
        },
        greenAccent: {
          100: "#0f2922",
          200: "#f1f1f1",
          300: "#2e7c67",
          400: "#3872FA",
          500: "#ffab00",
          600: "#70d8bd",
          700: "#94e2cd",
          800: "#b7ebde",
          900: "#dbf5ee",
        },
        redAccent: {
          100: "#2c100f",
          200: "#58201e",
          300: "#832f2c",
          400: "#af3f3b",
          500: "#db4f4a",
          600: "#e2726e",
          700: "#e99592",
          800: "#f1b9b7",
          900: "#f8dcdb",
        },
        blueAccent: {
          100: "#1d2f4a",
          200: "#274673",
          300: "#1e529c",
          400: "#674704",
          500: "#3988f7",
          600: "#4f95f7",
          700: "#007bff",
          800: "#90baf5",
          900: "#cbddf7",
        },
      }),
});


// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.primary[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[500],
            },
          }
        : {
            primary: {
              main: colors.primary[100],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: "#b2c9ff",
            },
          }),
    },
    components: {
      MuiCheckbox: {
        styleOverrides: {
          root: {
            width: "16px",
            height: "16px",
            padding: "4px",
            borderRadius: "0px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              backgroundColor: colors.blueAccent[200],
            },
            "&.Mui-checked": {
              color: colors.greenAccent[500],
              backgroundColor: colors.greenAccent[400],
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
            },
            "&.MuiCheckbox-indeterminate": {
              backgroundColor: colors.redAccent[400],
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? colors.primary[500] : "light",
            border: "1px solid #ccc",
          },
          columnHeader: {
            backgroundColor: "#f1f1f1", // Set the header background color to #f1f1f1
            color: mode === "dark" ? "white" : "#666666", // Adjust text color based on mode
            fontWeight: "bold", // Optional: Make text bold
          },
          checkboxInput: {
            backgroundColor: "transparent",
            borderRadius: "0px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
            "&:hover": {
              backgroundColor: colors.blueAccent[200],
            },
            "&.Mui-checked": {
              color: colors.greenAccent[500],
              backgroundColor: colors.greenAccent[400],
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
            },
          },
          columnHeaderCheckbox: {
            padding: "0px 10px",
          },
        },
      },
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("light");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};